/** @format */

import type {ReportData} from 'ui';

/**
 * Ответ API → снимок для страницы.
 *
 * Переименований здесь нет и не будет: контракт страницы — зеркало
 * `ProjectReportData`. Адаптер существует ради денег: они объявлены
 * скаляром `Decimal`, который приходит либо числом, либо строкой, а
 * страница форматирует их разрядами и складывает.
 *
 * Приведение точечное, а не сплошным обходом дерева: список денежных
 * полей известен, и «пройтись по всему и привести всё, что похоже на
 * число» сломало бы, например, идентификаторы кампаний.
 */

/** Сырой ответ: `Decimal` в кодогене объявлен как `string | number`. */
export type Decimal = string | number | null | undefined;

interface RawAmount {
  value: Decimal;
  unit: string;
  of?: Decimal;
}

interface RawTarget {
  amount?: RawAmount | null;
  bound?: string | null;
  text?: string | null;
}

/** Пустое значение остаётся пустым: ноль и «не знаем» — разные вещи. */
export function num(value: Decimal): number | null {
  if (value === null || value === undefined) return null;
  // Number('') === 0, а не NaN: пустую строку надо отсечь до приведения,
  // иначе «значения нет» превратится в ноль рублей.
  if (typeof value === 'string' && value.trim() === '') return null;
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function amount(raw?: RawAmount | null) {
  if (!raw) return null;
  return {...raw, value: num(raw.value) ?? 0, of: num(raw.of)};
}

function target(raw: RawTarget) {
  return {...raw, amount: amount(raw.amount)};
}

// Снимок приходит из кодогена со своими типами; на входе принимаем его
// структурно, на выходе отдаём контракт страницы.

export function toReportData(snapshot: any): ReportData {
  const {meta, project} = snapshot;

  return {
    meta,
    project: {
      ...project,
      riskDailySpend: num(project.riskDailySpend),
      spend: num(project.spend),
      risk: num(project.risk),
      kpi: project.kpi
        ? {...project.kpi, plan: num(project.kpi.plan), fact: num(project.kpi.fact)}
        : project.kpi,
      clients: project.clients
        ? {
            ...project.clients,
            budget: num(project.clients.budget) ?? 0,
            cplPlan: num(project.clients.cplPlan),
            cplFact: num(project.clients.cplFact),
          }
        : project.clients,
      risks: project.risks?.map((row: {spend: Decimal}) => ({...row, spend: num(row.spend)})),
      riskRest: project.riskRest?.map((row: {spend: Decimal}) => ({
        ...row,
        spend: num(row.spend),
      })),
      zeroResult: project.zeroResult
        ? {
            ...project.zeroResult,
            spend: num(project.zeroResult.spend),
            campaigns: project.zeroResult.campaigns?.map(
              (campaign: {spend: Decimal; kpiFact: Decimal}) => ({
                ...campaign,
                spend: num(campaign.spend),
                kpiFact: num(campaign.kpiFact),
              }),
            ),
          }
        : project.zeroResult,
      campaigns: project.campaigns?.map(
        (campaign: {spend: Decimal; kpiFact: Decimal; kpiPlan: Decimal}) => ({
          ...campaign,
          spend: num(campaign.spend),
          kpiFact: num(campaign.kpiFact),
          kpiPlan: num(campaign.kpiPlan),
        }),
      ),
      control: project.control
        ? {
            ...project.control,
            expectations: project.control.expectations.map(
              (item: {from: RawTarget; to: RawTarget}) => ({
                ...item,
                from: target(item.from),
                to: target(item.to),
              }),
            ),
          }
        : project.control,
    },
  } as ReportData;
}
